// Helpers for the new Base44 order fields (delivery_date, fulfillment_type,
// customer_email, notes, po_number, store). Everything is defensive — if a
// field is missing we render nothing.

const ONE_DAY = 24 * 60 * 60 * 1000;

/** Parse an ISO date string (YYYY-MM-DD) into a local Date at midnight. */
export function parseDeliveryDate(value) {
  if (!value) return null;
  if (typeof value !== "string") return null;
  // YYYY-MM-DD → treat as local midnight to avoid timezone off-by-one
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Compact display label: "Tue Jun 24". */
export function formatDeliveryDate(value) {
  const d = parseDeliveryDate(value);
  if (!d) return null;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/**
 * Returns { label, tone } for a delivery-date badge.
 * tone ∈ "today" | "tomorrow" | "soon" | "future" | "overdue"
 */
export function deliveryBadge(value) {
  const d = parseDeliveryDate(value);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d.getTime() - today.getTime()) / ONE_DAY);

  if (diffDays === 0) return { label: "Today", tone: "today" };
  if (diffDays === 1) return { label: "Tomorrow", tone: "tomorrow" };
  if (diffDays > 1 && diffDays <= 7) return { label: `in ${diffDays}d`, tone: "soon" };
  if (diffDays > 7) return { label: `in ${diffDays}d`, tone: "future" };
  // diffDays < 0 → overdue
  return { label: `Overdue ${Math.abs(diffDays)}d`, tone: "overdue" };
}

export const BADGE_STYLES = {
  today:    { bg: "#FEE2E2", text: "#991B1B", border: "#EF4444" },
  tomorrow: { bg: "#FFEDD5", text: "#9A3412", border: "#F97316" },
  soon:     { bg: "#FEF3C7", text: "#854D0E", border: "#F59E0B" },
  future:   { bg: "#E0E7FF", text: "#3730A3", border: "#6366F1" },
  overdue:  { bg: "#7F1D1D", text: "#FECACA", border: "#DC2626" },
};

/** Pretty-cased fulfillment type ("delivery" → "Delivery"). null if missing. */
export function formatFulfillmentType(value) {
  if (!value || typeof value !== "string") return null;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** True if an order has any of the new optional fields populated. */
export function hasOrderMeta(order) {
  if (!order) return false;
  return !!(
    order.delivery_date ||
    order.fulfillment_type ||
    order.customer_email ||
    order.notes ||
    order.po_number ||
    order.store
  );
}
